import React from 'react';
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BoltIcon from '@mui/icons-material/Bolt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as DomLink } from 'react-router-dom';
import { useAppContext } from './AppContext';

const LOGO_SRC = '/Order-Master-Logo-SVG.svg';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'my', label: 'Burmese', flag: '🇲🇲' },
];

const FEATURES = [
  {
    title: 'Fast Order Flow',
    body: 'Create, manage, and release orders with minimal steps and clear status tracking.',
    icon: <BoltIcon />,
  },
  {
    title: 'Inventory Confidence',
    body: 'Track stock and products in one place so your team never sells what is not available.',
    icon: <Inventory2Icon />,
  },
  {
    title: 'Role-Based Access',
    body: 'Give owners, managers, and cashiers the right tools without exposing sensitive settings.',
    icon: <VerifiedUserIcon />,
  },
  {
    title: 'Sales Visibility',
    body: 'See sales, reports, and performance at a glance to keep decisions data driven.',
    icon: <BarChartIcon />,
  },
];

const PRICING = [
  {
    plan: 'Trial',
    price: 'Free',
    description: 'Start with free 100 credits.',
    items: ['Core order flow', 'Stock tracking', 'Role-based access', 'Promotion tools', 'Full reports'],
  },
  {
    plan: 'Premium',
    price: '1 Credit = 100 Kyat',
    description: 'After enjoying trial mode',
    items: ['Core order flow', 'Stock tracking', 'Role-based access', 'Promotion tools', 'Full reports'],
  },
  {
    plan: 'Enterprise',
    price: 'Custom',
    description: 'Multi-branch operations and tailored support.',
    items: ['All basic features','Multi-shop setup', 'Priority support', 'Custom workflows'],
  },
];

const FAQS = [
  {
    q: 'How do I sign in?',
    a: 'Use your email and password on the sign in screen. Google sign in is also available.',
  },
  {
    q: 'Can I manage multiple staff roles?',
    a: 'Yes. Owners, managers, operators, and cashiers can be assigned different access levels.',
  },
  {
    q: 'How many reports are available?',
    a: 'You can view daily income report, financial report, order report, end of day report, shift report and customer analysis report.',
  },
];

const SCREENSHOTS = [
  { src: '/view-order.png', alt: 'View Orders', label: 'View Orders' },
  { src: '/manage-order.png', alt: 'Manage Orders', label: 'Manage Orders' },
  { src: '/daily-report.png', alt: 'Daily Report', label: 'Daily Report' },
  { src: '/financial-report.png', alt: 'Financial Report', label: 'Financial Report' },
];

const LandingPage = () => {
  const { t, language, setLanguage } = useAppContext();
  const [activeShot, setActiveShot] = React.useState(0);
  const totalShots = SCREENSHOTS.length;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', overflowX: 'hidden' }}>
      <AppBar position="sticky" color="transparent" elevation={0}>
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
            <Box
              component="img"
              src={LOGO_SRC}
              alt={t('Order Master')}
              sx={{ height: 40, width: 'auto' }}
            />
          </Box>

          <Box
            sx={{
              mt: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              size="small"
              renderValue={(val) => {
                const option = LANGUAGE_OPTIONS.find((opt) => opt.value === val);
                return option ? `${option.flag} ${t(option.label)}` : val;
              }}
              sx={{
                color: 'text.primary',
                borderColor: 'rgba(0,0,0,0.3)',
                minWidth: { xs: 110, sm: 140 },
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.3)',
                },
              }}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box component="span" sx={{ mr: 1 }}>
                    {option.flag}
                  </Box>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Select>
            <Button component={DomLink} to="/login" variant="contained" color="primary">
              {t('Sign in')}
            </Button>
          </Box>
        </Toolbar>
        <Divider />
      </AppBar>

      <Box
        sx={{
          background: 'linear-gradient(180deg, rgba(106,27,154,0.08) 0%, rgba(255,255,255,1) 60%)',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Typography variant="h2" component="h1">
                  {t('Order management that keeps your shop in sync')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('Order Master helps teams move faster from sale to fulfillment with clean workflows, clear roles, and real-time insight.')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    component={DomLink}
                    to="/login"
                    variant="contained"
                    color="primary"
                    size="large"
                  >
                    {t('Sign in')}
                  </Button>
                  <Button
                    component="a"
                    href="#pricing"
                    variant="outlined"
                    color="primary"
                    size="large"
                  >
                    {t('View pricing')}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="h5">{t('Product screenshots')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        aria-label="Previous screenshot"
                        size="small"
                        onClick={() => setActiveShot((prev) => (prev - 1 + totalShots) % totalShots)}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Next screenshot"
                        size="small"
                        onClick={() => setActiveShot((prev) => (prev + 1) % totalShots)}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      aspectRatio: '16 / 9',
                    }}
                  >
                    <Box
                      component="img"
                      src={SCREENSHOTS[activeShot].src}
                      alt={t(SCREENSHOTS[activeShot].alt)}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 12,
                        bottom: 12,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        color: 'common.white',
                        fontSize: 12,
                      }}
                    >
                      {t(SCREENSHOTS[activeShot].label)}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      overflowX: 'auto',
                      pb: 0.5,
                      scrollSnapType: 'x mandatory',
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 999 },
                    }}
                  >
                    {SCREENSHOTS.map((shot, index) => (
                      <ButtonBase
                        key={`${shot.label}-${index}`}
                        onClick={() => setActiveShot(index)}
                        sx={{
                          flex: '0 0 auto',
                          width: 120,
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px solid',
                          borderColor: index === activeShot ? 'primary.main' : 'divider',
                          scrollSnapAlign: 'start',
                        }}
                      >
                        <Box
                          component="img"
                          src={shot.src}
                          alt={t(shot.alt)}
                          sx={{ width: '100%', height: 72, objectFit: 'cover', display: 'block' }}
                        />
                      </ButtonBase>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ mb: 3 }}>
            {t('Features')}
          </Typography>
          <Grid container spacing={3}>
            {FEATURES.map((feature) => (
              <Grid key={feature.title} item xs={12} md={6}>
                <Card elevation={0} sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {t(feature.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(feature.body)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="pricing" sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ mb: 3 }}>
            {t('Pricing')}
          </Typography>
          <Grid container spacing={3}>
            {PRICING.map((tier) => (
              <Grid key={tier.plan} item xs={12} md={4}>
                <Card elevation={0} sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="h5">{t(tier.plan)}</Typography>
                      <Typography variant="h4" color="primary">
                        {t(tier.price)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(tier.description)}
                      </Typography>
                      <Divider />
                      <Stack spacing={1}>
                        {tier.items.map((item) => (
                          <Typography key={item} variant="body2">
                            {t(item)}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ mb: 3 }}>
            {t('FAQ')}
          </Typography>
          <Stack spacing={2}>
            {FAQS.map((faq) => (
              <Accordion key={faq.q} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t(faq.q)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {t(faq.a)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: 4, borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('Order Master. Built for busy teams.')}
            </Typography>
            <Button component={DomLink} to="/login" variant="contained" color="primary">
              {t('Sign in')}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
